import type { MetricValue } from '@ops/contracts'
import { deltaBaseOf, periodoAnterior } from '@ops/contracts'

import { listaEs, plural } from '../../lib/format'
import { Card, HealthDot, Icon, KpiCard } from '../../ui'

/**
 * Zona 3 del tablero de riesgo: **lo que está sano, en una línea y colapsado**.
 * Si un frente está bien no pide atención, y ocupar media pantalla con tarjetas
 * verdes es exactamente lo que hace que no se vea lo que está roto.
 *
 * Es un `<details>` nativo: se abre con teclado, lo anuncia el lector de
 * pantalla y no necesita estado ni un componente nuevo. El resumen dice cuántas
 * y cuáles, así que la línea cerrada ya informa.
 */
export interface ZonaSanaProps {
  metricas: readonly MetricValue[]
  /** Con esto el delta de cada tarjeta se tiñe según su métrica. */
  direccionDe: (slug: string) => 'higher_better' | 'lower_better' | undefined
  onOpenDetail?: (metric: MetricValue) => void
  now: Date
}

export function ZonaSana({
  metricas,
  direccionDe,
  onOpenDetail,
  now,
}: ZonaSanaProps) {
  if (metricas.length === 0) {
    return (
      <Card padding="sm" hundida>
        <p className="inline-flex items-center gap-2 text-meta text-muted-strong">
          <Icon name="sin-dato" size={13} />
          Ningún frente está en target todavía: no hay nada para colapsar acá.
        </p>
      </Card>
    )
  }

  return (
    <Card padding="none" hundida>
      <details data-testid="zona-sana">
        <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-4 py-3 text-meta text-text marker:content-none">
          <HealthDot health="ok" size="sm" showLabel={false} />
          {/* El conteo y la lista van en el mismo nodo de texto: partidos en
              dos, el resumen se lee "1frente" para un lector de pantalla. */}
          <span className="text-muted-strong">
            <b className="tabular font-semibold text-text">
              {metricas.length}
            </b>{' '}
            {plural(metricas.length, 'frente en target', 'frentes en target')}:{' '}
            {listaEs(metricas.map((m) => m.nombre))}
          </span>
          <Icon
            name="chevron"
            size={12}
            className="ml-auto text-muted-strong"
            title="Ver el detalle de los frentes en target"
          />
        </summary>

        <div className="grid grid-cols-1 gap-3 border-t border-border p-4 sm:grid-cols-2 xl:grid-cols-4">
          {metricas.map((metric) => {
            const periodoBase = periodoAnterior(metric.periodo)
            return (
              <KpiCard
                key={metric.slug}
                metric={metric}
                base={
                  periodoBase === null
                    ? null
                    : deltaBaseOf(metric, periodoBase)
                }
                direccion={direccionDe(metric.slug)}
                onOpenDetail={
                  onOpenDetail === undefined
                    ? undefined
                    : () => onOpenDetail(metric)
                }
                now={now}
              />
            )
          })}
        </div>
      </details>
    </Card>
  )
}
